import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";
import maipLogo from "@/assets/maip-logo.png";

const PasswordProtection = () => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("verify-site-password", {
        body: { password },
      });

      if (error) throw error;

      if (data?.valid) {
        // Passwort ist korrekt - in localStorage speichern
        localStorage.setItem("siteAccessGranted", "true");
        toast({
          title: "Zugang gewährt",
          description: "Willkommen auf MAIP.info",
        });
        // Seite neu laden, damit App.tsx den Zugang erkennt
        window.location.href = "/";
      } else {
        toast({
          title: "Falsches Passwort",
          description: "Bitte versuchen Sie es erneut.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      toast({
        title: "Fehler",
        description: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 px-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl shadow-2xl border border-border p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src={maipLogo} alt="MAIP Logo" className="h-16" />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Zugangsgeschützt</h1>
            <p className="text-muted-foreground">
              Diese Seite befindet sich im Pre-Launch. Bitte geben Sie das Zugangswort ein.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password">Zugangswort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Geben Sie das Zugangswort ein"
                  required
                  className="pr-10"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
              {isLoading ? "Wird überprüft..." : "Zugang anfordern"}
            </Button>
          </form>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-muted/50 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground text-center">
              Sie haben noch kein Zugangswort? Wenden Sie sich bitte an das MAIP-Team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordProtection;
